import { api } from "@/lib/api";
import {
  TourAgency,
  TourAgencyDomain,
  TourAgencyDtoSchema,
  tourAgencyFromDto,
  tourAgencyToDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getAllTourAgenciesQueryOptions,
  getTourAgenciesQueryOptions,
  getTourAgencyQueryOptions,
} from "./get.hooks";

const toastDomainMessageMap: Record<TourAgencyDomain, string> = {
  agency: "toast.tourAgencyTourUpdated",
  tour: "toast.tourAgencyAgencyUpdated",
};

const toastDomainErrorMessageMap: Record<TourAgencyDomain, string> = {
  agency: "toast.updateTourAgencyTourError",
  tour: "toast.updateTourAgencyAgencyError",
};

export function useUpdateTourAgency(
  domain: TourAgencyDomain,
  domainId: string,
  opts?: {
    onSuccess?: (tourAgency: TourAgency) => void;
    onError?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (tourAgency: TourAgency) => {
      const dto = tourAgencyToDto(tourAgency);
      const result = await api.put(
        "/api/tour-agencies",
        dto,
        TourAgencyDtoSchema,
      );
      return tourAgencyFromDto(result);
    },
    onSuccess: (tourAgency) => {
      queryClient.setQueryData(
        getTourAgenciesQueryOptions(domain, domainId).queryKey,
        (old) =>
          old?.filter((ta) => (ta.id !== tourAgency.id ? ta : tourAgency)),
      );
      queryClient.setQueryData(getAllTourAgenciesQueryOptions.queryKey, (old) =>
        old?.filter((ta) => (ta.id !== tourAgency.id ? ta : tourAgency)),
      );
      queryClient.setQueryData(
        getTourAgencyQueryOptions(tourAgency.id).queryKey,
        () => tourAgency,
      );
      toast({
        title: toastDomainMessageMap[domain],
        variant: "success",
      });
      opts?.onSuccess?.(tourAgency);
    },
    onError: () => {
      toast({
        title: toastDomainErrorMessageMap[domain],
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
